import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import { useI18n } from '../i18n';
import { showToast } from '../utils/toast';

/**
 * Watches vault for pending master key transfer requests and shows a toast
 * on the device that already has the master key, prompting the user to approve.
 *
 * Should be called once in a top-level component (e.g. MainLayout or App).
 */
export function useMkApprovalWatcher() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const shownForRef = useRef<Set<string>>(new Set());

  const masterKey = useAuthStore((state) => state.masterKey);
  const localDeviceIds = useAuthStore((state) => Object.keys(state.devices));
  const pendingMkRequests = useVaultStore((state) => state.vaultData?.pendingMkRequests);

  useEffect(() => {
    if (!masterKey || localDeviceIds.length === 0) return;
    if (!pendingMkRequests) return;

    const localDeviceIdSet = new Set(localDeviceIds);

    for (const [requestingDeviceId] of Object.entries(pendingMkRequests)) {
      // Skip requests created by any device already present on this client.
      if (localDeviceIdSet.has(requestingDeviceId)) continue;
      // Only show toast once per requestingDeviceId per session.
      if (shownForRef.current.has(requestingDeviceId)) continue;

      shownForRef.current.add(requestingDeviceId);

      showToast({
        message: t('mkTransferApproval.title'),
        variant: 'info',
        durationMs: 15000,
        actionLabel: t('mkTransferApproval.approve'),
        onAction: () => {
          navigate('/master-key/approve', {
            state: { requestingDeviceId }
          });
        }
      });
    }
  }, [localDeviceIds, masterKey, navigate, pendingMkRequests, t]);
}
